import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { ButtonComponent, AutofocusDirective, StatusIconComponent, TooltipDirective } from '@mode/capra';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogHeaderComponent, StatusBannerComponent } from '@mode/shared/ui';
import { Dataset, DatasetFacade, DatasetPublishingFlagStates } from '@mode/dataset/contract';
import {
  EnhancedDatasetResult,
  FeatureFlag,
  FeatureFlagsFacade,
  ProducerType,
  ProducerTypes,
  SchedulesTypes,
} from '@mode/shared/contract-common';
import { combineLatestWith, map, startWith, take } from 'rxjs/operators';
import { PublishDatasetForm } from './publish-dataset-form';
import { DatasetMetadataComponent } from '@mode/dataset/ui';
import { SchedulesFacade } from '@mode/dataset/domain';
import { SchedulesFacade as SharedSchedulesFacade } from '@mode/shared/schedules/contract';
import { DatasourceFacade } from '@mode/accounts/domain';
import { getInitialFlagValue } from '@mode/shared/util-js';
import { Observable, of, Subject } from 'rxjs';
import { DataViewFacade, SwitchViewFacade } from '@mode/switch-views/contract';
import { SharedUtilSegmentModule } from '@mode/shared/util-segment';

@Component({
  selector: 'mode-publish-dataset-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    DialogHeaderComponent,
    FormsModule,
    ReactiveFormsModule,
    AutofocusDirective,
    StatusBannerComponent,
    StatusIconComponent,
    TooltipDirective,
    DatasetMetadataComponent,
    SharedUtilSegmentModule,
  ],
  providers: [TitleCasePipe],
  templateUrl: './publish-dataset-dialog.component.html',
  styleUrls: ['./publish-dataset-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublishDatasetDialogComponent implements OnInit {
  readonly form = new PublishDatasetForm();
  readonly nameNormalizationInfo = `Will be converted to an alphanumeric and underscore format on import and in TML. Names within a Collection must be unique.`;
  switchViewToken: string;
  publishLocalDatasetEnabled = getInitialFlagValue(FeatureFlag.NotebookPublishExportedDatasets);
  private destroyed$ = new Subject<void>();

  isSheetDataset$!: Observable<boolean>;
  schedules$!: Observable<string>;
  datasourceDisplayName$!: Observable<string>;
  nuggetAlphaFlow$!: Observable<boolean>;
  hasCalcFields$!: Observable<boolean>;
  isNotebookCell$!: Observable<boolean>;

  dataset$!: Observable<Dataset>;

  enhancedDatasetResult$!: Observable<EnhancedDatasetResult | undefined>;

  @Output() publish = new EventEmitter<{
    newDatasetName: string;
    datasetToken: string;
    producerType: ProducerType;
    queryToken?: string;
  }>();

  constructor(
    protected dialogRef: MatLegacyDialogRef<PublishDatasetDialogComponent>,
    private datasetFacade: DatasetFacade,
    private dataViewFacade: DataViewFacade,
    private datasourceFacade: DatasourceFacade,
    private switchViewFacade: SwitchViewFacade,
    private schedulesFacade: SchedulesFacade,
    private sharedSchedulesFacade: SharedSchedulesFacade,
    private titleCasePipe: TitleCasePipe,
    private featureFlagsFacade: FeatureFlagsFacade,
    @Inject(MAT_LEGACY_DIALOG_DATA)
    private data: {
      switchViewToken: string;
    }
  ) {
    this.switchViewToken = data.switchViewToken;
  }

  ngOnInit() {
    if (this.publishLocalDatasetEnabled) {
      // support reusable and local datasets
      this.enhancedDatasetResult$ = this.switchViewFacade.getEnhancedDataseResult(this.switchViewToken);
      this.dataset$ = this.datasetFacade.dataset$;
      this.enhancedDatasetResult$.pipe(take(1)).subscribe({
        next: (dataset) => {
          this.isNotebookCell$ = of(dataset?.producerType === ProducerTypes.NotebookCell);
          this.isSheetDataset$ = of(dataset?.producerType === ProducerTypes.GoogleSheet);
          this.schedulesFacade.loadSchedules(dataset?.datasetToken ?? '');
          if (dataset?.producerType === ProducerTypes.NotebookCell) {
            this.form.updateName(dataset?.queryName ?? '');
          } else {
            this.dataset$.pipe(take(1)).subscribe({
              next: (dataset) => {
                this.form.updateName(dataset.details?.name ?? '');
              },
            });
          }
        },
      });
      this.hasCalcFields$ = this.dataViewFacade.isCalcBySwitchViewTokenFromSwitchViewAttr(this.switchViewToken);
      this.schedules$ = this.schedulesFacade.schedules$.pipe(
        combineLatestWith(this.isSheetDataset$),
        map(([schedules, isSheetDataset]) => this.generateScheduleString(schedules, isSheetDataset))
      );
      this.datasourceDisplayName$ = this.datasourceFacade.datasources$.pipe(
        combineLatestWith(this.enhancedDatasetResult$),
        map(([datasources, dataset]) => {
          if (dataset?.producerType === ProducerTypes.GoogleSheet) return 'Google Sheets';
          return datasources.find((ds) => ds.id === dataset?.datasourceId)?.displayName ?? '';
        })
      );
    } else {
      // support reusable datasets
      this.dataset$ = this.datasetFacade.dataset$;
      this.isSheetDataset$ = this.datasetFacade.isSheetDataset$;
      this.dataset$.pipe(take(1)).subscribe({
        next: (dataset) => {
          this.form.updateName(dataset.details?.name ?? '');
          this.schedulesFacade.loadSchedules(dataset.token);
        },
      });
      this.hasCalcFields$ = this.dataset$.pipe(map((dataset) => this.datasetFacade.hasCalcFields(dataset)));
      this.schedules$ = this.schedulesFacade.schedules$.pipe(
        combineLatestWith(this.isSheetDataset$),
        map(([schedules, isSheetDataset]) => this.generateScheduleString(schedules, isSheetDataset))
      );
      this.datasourceDisplayName$ = this.datasourceFacade.datasources$.pipe(
        combineLatestWith(this.dataset$),
        map(([datasources, dataset]) => {
          if (dataset.result.producerType === ProducerTypes.GoogleSheet) return 'Google Sheets';
          return datasources.find((ds) => ds.id === dataset.result.datasourceId)?.displayName ?? '';
        })
      );
    }

    this.nuggetAlphaFlow$ = this.featureFlagsFacade
      .featureValueAsObservable<DatasetPublishingFlagStates>(FeatureFlag.DatasetPublishing)
      .pipe(
        startWith(DatasetPublishingFlagStates.DISABLED),
        map((flag) => flag === DatasetPublishingFlagStates.ENABLED_ALPHA)
      );
  }

  publishDataset(): void {
    if (this.publishLocalDatasetEnabled) {
      // support reusable and local datasets
      this.enhancedDatasetResult$.pipe(take(1)).subscribe({
        next: (dataset) =>
          this.publish.emit({
            newDatasetName: this.form.name.value,
            datasetToken: dataset?.datasetToken ?? '',
            producerType: dataset?.producerType ?? ProducerTypes.SqlQuery,
            queryToken: dataset?.queryToken,
          }),
      });
    } else {
      // support reusable datasets
      this.dataset$.pipe(take(1)).subscribe({
        next: (dataset) =>
          this.publish.emit({
            newDatasetName: this.form.name.value,
            datasetToken: dataset.token,
            producerType: dataset.result.producerType,
          }),
      });
    }

    this.close();
  }

  openScheduleModal() {
    if (this.publishLocalDatasetEnabled) {
      // support reusable and local datasets
      this.enhancedDatasetResult$.pipe(take(1)).subscribe({
        next: (dataset) => this.openModalWithToken(dataset?.datasetToken ?? ''),
      });
    } else {
      // support reusable datasets
      this.dataset$.pipe(take(1)).subscribe({
        next: (dataset) => this.openModalWithToken(dataset.token),
      });
    }
  }

  close() {
    this.dialogRef.close();
  }

  private openModalWithToken(reportToken: string) {
    this.sharedSchedulesFacade.openScheduleModal({ token: '', reportToken } as SchedulesTypes.Schedule);
    this.close();
  }

  private generateScheduleString(schedules: SchedulesTypes.Schedule[], isSheetDataset: boolean): string {
    return schedules
      .map((schedule, index) => {
        const isFirst = index === 0;
        const isLast = index === schedules.length - 1;
        let result = '';

        if (isFirst) {
          result += isSheetDataset ? 'Syncs ' : 'Runs ';
        }

        result += `${this.titleCasePipe.transform(schedule.frequency)} at ${schedule.hour} ${schedule.timeZone}`;

        if (!isLast) {
          result += ', ';
        }
        return result;
      })
      .join('');
  }
}
